import React, { useState } from 'react';
import { Mail, Phone, MapPin, MessageCircle, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { CONTACT } from '../mock';
import { trackLead, identify } from '../lib/analytics';

const NAVY = '#1a3a5c';
const TEAL = '#3d94b8';

const buildMessage = (f) =>
  [
    'Hallo AvantiTutor!',
    `Name: ${f.name}`,
    f.email ? `E-Mail: ${f.email}` : null,
    f.phone ? `Telefon: ${f.phone}` : null,
    `Betreff: ${f.subject}`,
    '',
    f.message,
  ].filter((l) => l !== null).join('\n');

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const sendWhatsApp = (e) => {
    e.preventDefault();
    identify(form.email);
    trackLead('contact_form_whatsapp', { subject: form.subject }, form.email);
    window.open(`${CONTACT.whatsapp}?text=${encodeURIComponent(buildMessage(form))}`, '_blank', 'noopener');
    toast({ title: 'WhatsApp öffnet sich', description: 'Deine Nachricht ist vorausgefüllt – nur noch abschicken.' });
  };

  const mailtoHref = `mailto:${CONTACT.email}?subject=${encodeURIComponent(form.subject || 'Anfrage über die Website')}&body=${encodeURIComponent(buildMessage(form))}`;

  const infos = [
    { icon: MessageCircle, title: 'WhatsApp', text: CONTACT.phone, sub: 'Schnellste Antwort', href: CONTACT.whatsapp, testId: 'contact-info-whatsapp' },
    { icon: Phone, title: 'Telefon', text: CONTACT.phone, sub: 'Mo–Fr, 9–18 Uhr', href: `tel:${CONTACT.phoneRaw}`, testId: 'contact-info-phone' },
    { icon: Mail, title: 'E-Mail', text: CONTACT.email, sub: 'Antwort innerhalb 24h', href: `mailto:${CONTACT.email}`, testId: 'contact-info-email' },
    { icon: MapPin, title: 'Adresse', text: CONTACT.address, sub: 'Online deutschlandweit', testId: 'contact-info-address' },
  ];

  return (
    <div>
      <section className="hero-bg py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <span className="font-semibold uppercase tracking-wider text-xs" style={{ color: TEAL }}>Kontakt</span>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight" style={{ color: NAVY }}>Lass uns reden</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">Wir freuen uns auf deine Nachricht – egal ob für ein Erstgespräch, Fragen oder Feedback.</p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1fr_1.2fr] gap-10">
          <div className="space-y-4">
            {infos.map((c) => {
              const Wrapper = c.href ? 'a' : 'div';
              const props = c.href ? { href: c.href, target: c.href.startsWith('http') ? '_blank' : undefined, rel: 'noreferrer' } : {};
              return (
                <Wrapper key={c.title} data-testid={c.testId} {...props} className="card-soft p-6 flex gap-4 items-start block transition-transform hover:-translate-y-0.5">
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#e6f2f7' }}>
                    <c.icon className="h-6 w-6" style={{ color: TEAL }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: NAVY }}>{c.title}</h3>
                    <div className="font-medium mt-0.5 text-slate-800">{c.text}</div>
                    <div className="text-sm text-slate-500">{c.sub}</div>
                  </div>
                </Wrapper>
              );
            })}
            <div className="rounded-2xl p-6 text-white" style={{ background: `linear-gradient(135deg, ${TEAL} 0%, #2d7d9a 100%)` }}>
              <div className="flex items-center gap-2 font-bold"><Globe className="h-5 w-5" /> {CONTACT.website}</div>
              <p className="mt-1 text-sm text-white/90">Unterricht komplett online – egal wo du in Deutschland wohnst.</p>
            </div>
          </div>

          <form onSubmit={sendWhatsApp} data-testid="contact-form" className="card-soft p-7 md:p-9 space-y-4">
            <h2 className="text-2xl font-extrabold" style={{ color: NAVY }}>Schreib uns eine Nachricht</h2>
            <p className="text-sm text-slate-600 -mt-2">Deine Nachricht wird in WhatsApp vorausgefüllt – du musst sie nur noch abschicken.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">Name</label>
                <Input data-testid="contact-name" required value={form.name} onChange={set('name')} className="mt-1.5 h-11 rounded-xl" placeholder="Max Mustermann" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">E-Mail (optional)</label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={set('email')} className="mt-1.5 h-11 rounded-xl" placeholder="max@example.de" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">Telefon (optional)</label>
                <Input data-testid="contact-phone" value={form.phone} onChange={set('phone')} className="mt-1.5 h-11 rounded-xl" placeholder="+49 ..." />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Betreff</label>
                <Input data-testid="contact-subject" required value={form.subject} onChange={set('subject')} className="mt-1.5 h-11 rounded-xl" placeholder="Bedarfsanalyse ..." />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Nachricht</label>
              <Textarea data-testid="contact-message" required value={form.message} onChange={set('message')} rows={5} className="mt-1.5 rounded-xl" placeholder="Wie können wir dir helfen?" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
              <Button data-testid="contact-submit-whatsapp" type="submit" className="btn-whatsapp rounded-full h-12 px-6 w-full sm:w-auto">
                <MessageCircle className="mr-2 h-4 w-4" /> Per WhatsApp senden
              </Button>
              <a data-testid="contact-submit-mail" href={mailtoHref} className="text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:underline" style={{ color: '#2d7d9a' }}>
                <Mail className="h-4 w-4" /> oder per E-Mail senden
              </a>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
